import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CreateChancel, CustomNumericFormat, CustomizedSwitch, Loader } from 'components';
import { systemActions } from 'store';
import { FindLoad, Images } from 'utils';

const GET_ACTION = 'GET_ELIGIBILITY_AUTO_CHECK_CONFIG';
const UPDATE_ACTION = 'UPDATE_ELIGIBILITY_AUTO_CHECK_CONFIG';
const BATCH_ACTION = 'RUN_BATCH_ELIGIBILITY_CHECK';

export const EligibilityAutoCheckSettings = () => {
    const dispatch = useDispatch();
    const config = useSelector((state) => state.system.eligibilityAutoCheckConfig);
    const loading = FindLoad(GET_ACTION);
    const updating = FindLoad(UPDATE_ACTION);
    const batchRunning = FindLoad(BATCH_ACTION);
    // remind / remindDays → `staleAfterDays` (null = reminder off); only used while checks are manual
    const [form, setForm] = useState({ enabled: false, frequencyDays: 7, remind: true, remindDays: 3 });
    const [hasChanges, setHasChanges] = useState(false);

    const dateFormat = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    const lastRunDate = config?.lastRunDate
        ? new Date(config.lastRunDate).toLocaleDateString('en-US', dateFormat)
        : null;
    const lastAttemptDate = config?.lastAttemptDate
        ? new Date(config.lastAttemptDate).toLocaleDateString('en-US', dateFormat)
        : null;
    const lastAttemptError = config?.lastAttemptError || null;

    useEffect(() => {
        dispatch(systemActions.getEligibilityAutoCheckConfig());
    }, []);

    const formFromConfig = (c) => ({
        enabled: c.enabled,
        frequencyDays: c.frequencyDays,
        remind: c.staleAfterDays !== null,
        remindDays: c.staleAfterDays ?? 3,
    });

    useEffect(() => {
        if (config) {
            setForm(formFromConfig(config));
            setHasChanges(false);
        }
    }, [config]);

    const handleToggle = () => {
        setForm((prev) => ({ ...prev, enabled: !prev.enabled }));
        setHasChanges(true);
    };

    const handleFrequencyChange = (e, sourceInfo) => {
        if (sourceInfo?.source === 'event') {
            setForm((prev) => ({ ...prev, frequencyDays: e.floatValue }));
            setHasChanges(true);
        }
    };

    const handleRemindToggle = () => {
        setForm((prev) => ({ ...prev, remind: !prev.remind }));
        setHasChanges(true);
    };

    const handleRemindDaysChange = (e, sourceInfo) => {
        if (sourceInfo?.source === 'event') {
            setForm((prev) => ({ ...prev, remindDays: e.floatValue }));
            setHasChanges(true);
        }
    };

    const handleSave = () => {
        const { enabled, frequencyDays, remind, remindDays } = form;
        const staleAfterDays = remind ? (remindDays >= 1 ? remindDays : 3) : null;
        dispatch(systemActions.updateEligibilityAutoCheckConfig({ enabled, frequencyDays, staleAfterDays }));
        setHasChanges(false);
    };

    const handleReset = () => {
        if (config) {
            setForm(formFromConfig(config));
        }
        setHasChanges(false);
    };

    if (loading?.length) {
        return <Loader height={350} />;
    }

    return (
        <div className="supervision-settings-wrapper">
            <div>
            <div className="supervision-section">
                <div className="eligibility-auto-box">
                    <div className="eligibility-auto-head">
                        <div>
                            <p className="settings-subsection-title">Automated Eligibility Verification</p>
                            <p className="settings-subsection-desc" style={{ margin: 0 }}>
                                Automatically check insurance eligibility for all active clients on a recurring schedule.
                            </p>
                        </div>
                        <CustomizedSwitch checkedBgColor={'#0C66E4'} checked={form.enabled} handleClick={handleToggle} />
                    </div>

                    {form.enabled && (
                        <>
                            <div className="settings-field-row">
                                <span className="settings-field-label">Check Frequency</span>
                                <CustomNumericFormat
                                    className="settings-numeric-input"
                                    name="frequencyDays"
                                    value={form.frequencyDays}
                                    handleChangeMiles={handleFrequencyChange}
                                    suffix=" days"
                                    decimalScale={0}
                                    noError={true}
                                />
                            </div>
                            <p className="settings-field-hint" style={{ marginBottom: 0 }}>
                                How often to run eligibility checks for all active clients (in days). A client's icon
                                turns orange ("Needs Review") when their last check is older than this.
                            </p>
                        </>
                    )}

                    {lastRunDate && (
                        <p className="eligibility-last-run">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                                <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Last successful run: <span style={{color: '#172B4D', fontWeight: 600}}>{lastRunDate}</span>
                        </p>
                    )}
                    {lastAttemptDate && lastAttemptDate !== lastRunDate && (
                        <p className="eligibility-last-run" style={{color: lastAttemptError ? '#B42318' : '#172B4D'}}>
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                                <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Last attempt: <span style={{fontWeight: 600}}>{lastAttemptDate}</span>
                            {lastAttemptError && <span style={{marginLeft: 4}}>— {lastAttemptError}</span>}
                        </p>
                    )}
                </div>
            </div>

            <div className="supervision-section" style={{marginTop: 8}}>
                <div className="eligibility-manual">
                    <p className="settings-subsection-title">Manual Eligibility Check</p>
                    <p className="settings-subsection-desc">
                        Run a one-time batch check for all active clients. Results process in the background.
                    </p>

                    <div    className="eligibility-run-btn-box">
                    <button
                        className="eligibility-run-btn"
                        onClick={() => dispatch(systemActions.runBatchEligibilityCheck())}
                        disabled={!!batchRunning?.length}
                    >
                        <img
                            style={{filter: 'brightness(0) saturate(100%) invert(12%) sepia(39%) saturate(2067%) hue-rotate(194deg) brightness(93%) contrast(91%)'}}
                            src={Images.play}
                            alt="play"
                        />
                        {batchRunning?.length ? 'Running...' : 'Run Eligibility Check'}
                    </button>
                    </div>

                    {/* Only for manual checks — with automatic checks on, the icon follows the check frequency */}
                    {!form.enabled && (
                        <div style={{ marginTop: 16 }}>
                            <div className="eligibility-auto-head">
                                <div>
                                    <p className="settings-subsection-title">Remind to Re-check</p>
                                    <p className="settings-subsection-desc" style={{ margin: 0 }}>
                                        Turn a client's icon orange ("Needs Review") when their last check gets old. Turn off
                                        to keep icons green until the next check.
                                    </p>
                                </div>
                                <CustomizedSwitch
                                    checkedBgColor={'#0C66E4'}
                                    checked={form.remind}
                                    handleClick={handleRemindToggle}
                                />
                            </div>
                            {form.remind && (
                                <div className="settings-field-row">
                                    <span className="settings-field-label">Turn Icon Orange After</span>
                                    <CustomNumericFormat
                                        className="settings-numeric-input"
                                        name="remindDays"
                                        value={form.remindDays}
                                        handleChangeMiles={handleRemindDaysChange}
                                        suffix=" days"
                                        decimalScale={0}
                                        noError={true}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            </div>
                {hasChanges && (
                    <div style={{marginTop: 12}}>
                    <CreateChancel
                        type={'button'}
                        justifyContent="flex-end"
                        alignItems="flex-end"
                        disabled={!hasChanges}
                        loader={updating?.length}
                        create={'Save Changes'}
                        chancel={'Cancel'}
                        onCreate={handleSave}
                        onClose={handleReset}
                        buttonWidth="150px"
                    />
                    </div>
                )}
        </div>
    );
};
